import "../../css/GlobalStyles.css";
import "../../css/Carousel.css";
import useEmblaCarousel from "embla-carousel-react";
import { CSSProperties, memo, useEffect, useMemo, useRef } from "react";
import { Event, ThemeType } from "@markit/common.types";
import { addAlpha, Colors } from "../../utils/colors";
import { getCanonicalMediaUrls } from "@markit/common.utils";

export type MediaCarouselContainerProps = {
  event: Event;
  hardTheme?: ThemeType;
  selectedMediaIndex: number;
  setSelectedMediaIndex: (index: number) => void;
  onImageClick?: () => void;
  imageStyle?: CSSProperties;
  imageClassName?: string;
};

/**
 * Wrapper that shows a carousel and dots when there are multiple items.
 */
const MediaCarouselContainer = memo(function MediaCarouselContainerFn(
  props: MediaCarouselContainerProps
) {
  const {
    event,
    selectedMediaIndex,
    setSelectedMediaIndex,
    onImageClick,
    imageStyle,
    imageClassName,
  } = props;

  const DOTS_STYLE: CSSProperties = {
    backgroundColor: addAlpha(Colors.BLACK1, 0.5),
    padding: 7,
    borderRadius: 20,
    position: "absolute" as const,
    bottom: 10,
    left: "50%",
    transform: "translateX(-50%)",
    justifyContent: "center",
    gap: 7,
    zIndex: 2,
    pointerEvents: "auto" as const,
  };

  const mediaUrls = useMemo(() => getCanonicalMediaUrls(event), [event]);
  const multipleMedia = mediaUrls.length > 1;

  const emblaOptions = useMemo(
    () => ({
      loop: multipleMedia,
      align: "start" as const,
      watchDrag: multipleMedia,
    }),
    [multipleMedia]
  );

  const [emblaRef, emblaApi] = useEmblaCarousel(emblaOptions);

  const selectedMediaIndexRef = useRef(selectedMediaIndex);
  selectedMediaIndexRef.current = selectedMediaIndex;

  const urlKey = useMemo(() => mediaUrls.join("\0"), [mediaUrls]);

  useEffect(() => {
    emblaApi?.reInit();
  }, [emblaApi, urlKey]);

  useEffect(() => {
    if (!emblaApi) return;
    if (emblaApi.selectedScrollSnap() !== selectedMediaIndex) {
      emblaApi.scrollTo(selectedMediaIndex);
    }
  }, [emblaApi, selectedMediaIndex]);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => {
      const i = emblaApi.selectedScrollSnap();
      if (i !== selectedMediaIndexRef.current) {
        setSelectedMediaIndex(i);
      }
    };
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
    return () => {
      emblaApi.off("select", onSelect);
      emblaApi.off("reInit", onSelect);
    };
  }, [emblaApi, setSelectedMediaIndex]);

  const imgSharedStyle = useMemo(
    (): CSSProperties => ({
      userSelect: "none",
      WebkitUserSelect: "none",
      MozUserSelect: "none",
      msUserSelect: "none",
      width: "100%",
      height: "100%",
      objectFit: "cover",
      objectPosition: "center",
      display: "block",
      ...imageStyle,
    }),
    [imageStyle]
  );

  if (mediaUrls.length === 0) {
    return null;
  }

  return (
    <div className="embla-carousel-root" style={{ position: "relative" }}>
      <div className="embla-carousel__viewport" ref={emblaRef}>
        <div className="embla-carousel__container">
          {mediaUrls.map((url, index) => (
            <div key={`${url}-${index}`} className="embla-carousel__slide">
              <img
                src={url}
                alt={`event media ${index + 1}`}
                style={imgSharedStyle}
                draggable={false}
                className={imageClassName}
                onClick={onImageClick}
              />
            </div>
          ))}
        </div>
      </div>
      {multipleMedia ? (
        <div className="AlignedRow" style={DOTS_STYLE}>
          {mediaUrls.map((_, index) => (
            <button
              key={index}
              type="button"
              aria-label={`Go to slide ${index + 1}`}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedMediaIndex(index);
              }}
              style={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                border: "none",
                padding: 0,
                cursor: "pointer",
                backgroundColor:
                  selectedMediaIndex === index
                    ? Colors.WHITE
                    : addAlpha(Colors.WHITE, 0.5),
                transition: "background-color 0.2s ease, transform 0.2s ease",
                boxShadow: "0 1px 2px rgba(0,0,0,0.2)",
              }}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
});

export default MediaCarouselContainer;
